import { RefreshCw, X } from 'lucide-react'
import type { CallsListQuery } from '../api'

export interface FilterState {
  agent: string
  dispo: string
  min_overall: string
  dispo_mismatch: boolean
  since: string
  until: string
}

export const EMPTY_FILTERS: FilterState = {
  agent: '',
  dispo: '',
  min_overall: '',
  dispo_mismatch: false,
  since: '',
  until: '',
}

export function filtersToQuery(f: FilterState): CallsListQuery {
  const q: CallsListQuery = {}
  if (f.agent.trim()) q.agent = f.agent.trim()
  if (f.dispo.trim()) q.dispo = f.dispo.trim().toUpperCase()
  const n = Number(f.min_overall)
  if (f.min_overall !== '' && !Number.isNaN(n)) q.min_overall = n
  if (f.dispo_mismatch) q.dispo_mismatch = true
  if (f.since) q.since = f.since
  if (f.until) q.until = f.until
  return q
}

interface FilterBarProps {
  state: FilterState
  onChange: (next: FilterState) => void
  onRefresh: () => void
  agents?: Array<{ vici_user: string; full_name: string | null }>
}

const DISPOS = ['', 'SALE', 'XFER', 'A', 'AM', 'AL', 'B', 'NI', 'NA', 'N', 'DNC', 'CALLBK']

export function FilterBar({ state, onChange, onRefresh, agents }: FilterBarProps) {
  const patch = <K extends keyof FilterState>(k: K, v: FilterState[K]) =>
    onChange({ ...state, [k]: v })

  const anySet =
    state.agent || state.dispo || state.min_overall || state.dispo_mismatch || state.since || state.until

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <Field label="Agent">
        {agents?.length ? (
          <select
            value={state.agent}
            onChange={(e) => patch('agent', e.target.value)}
            className="w-40 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-200"
          >
            <option value="">All</option>
            {agents.map((a) => (
              <option key={a.vici_user} value={a.vici_user}>
                {a.full_name ? `${a.vici_user} — ${a.full_name}` : a.vici_user}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            placeholder="vici_user"
            value={state.agent}
            onChange={(e) => patch('agent', e.target.value)}
            className="w-32 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-200"
          />
        )}
      </Field>
      <Field label="Dispo">
        <select
          value={state.dispo}
          onChange={(e) => patch('dispo', e.target.value)}
          className="w-28 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-200"
        >
          {DISPOS.map((d) => (
            <option key={d || 'all'} value={d}>
              {d || 'All'}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Min overall">
        <input
          type="number"
          min={0}
          max={10}
          step={0.5}
          placeholder="0"
          value={state.min_overall}
          onChange={(e) => patch('min_overall', e.target.value)}
          className="w-20 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-200"
        />
      </Field>
      <Field label="Since">
        <input
          type="date"
          value={state.since}
          onChange={(e) => patch('since', e.target.value)}
          className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-200"
        />
      </Field>
      <Field label="Until">
        <input
          type="date"
          value={state.until}
          onChange={(e) => patch('until', e.target.value)}
          className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-200"
        />
      </Field>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={state.dispo_mismatch}
          onChange={(e) => patch('dispo_mismatch', e.target.checked)}
          className="h-4 w-4 rounded border-slate-600 bg-slate-900"
        />
        Only mismatches
      </label>
      <div className="ml-auto flex items-center gap-2">
        {anySet && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_FILTERS)}
            className="flex items-center gap-1 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
        <button
          type="button"
          onClick={onRefresh}
          className="flex items-center gap-1.5 rounded border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-200 hover:bg-slate-800"
          title="Refresh (R)"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  // Implicit-label pattern: <label>Text <input/></label> is valid HTML and
  // associates by containment, but Biome's static check can't see through
  // children. This is a deliberate, spec-compliant grouping.
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: implicit-label pattern, child is the input
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  )
}
